/*
  Authentication endpoints.

  Shapes follow docs/07-identity-rbac.md section I.1. The flow is two legs on purpose:
  POST /auth/login never returns an access token when a second factor is outstanding, it
  returns an opaque mfaToken. That way a password alone cannot be traded for an API
  credential, and the TOTP screen is a separate state rather than a hidden field.

  The access token from the final leg goes straight into the in-memory store and nowhere
  else. See src/lib/api/token-store.ts for why.
*/

import { request } from '../client';
import { isMockTransport } from '../config';
import { setAccessToken, clearAccessToken } from '../token-store';
import type { CurrentUser, MfaMethodType } from '../types';

export interface MfaChallenge {
  readonly kind: 'mfa_required';
  /** Opaque, short lived, single use. Not a bearer token for anything else. */
  readonly mfaToken: string;
  readonly methods: readonly MfaMethodType[];
}

export interface SignedIn {
  readonly kind: 'signed_in';
  readonly accessToken: string;
  readonly expiresAt: string;
}

export type LoginResult = MfaChallenge | SignedIn;

interface LoginWireResponse {
  readonly mfaRequired?: boolean;
  readonly mfaToken?: string;
  readonly mfaMethods?: readonly MfaMethodType[];
  readonly accessToken?: string;
  readonly expiresAt?: string;
}

function toLoginResult(body: LoginWireResponse): LoginResult {
  if (body.mfaRequired === true && body.mfaToken !== undefined) {
    return {
      kind: 'mfa_required',
      mfaToken: body.mfaToken,
      methods: body.mfaMethods ?? ['totp'],
    };
  }
  if (body.accessToken !== undefined && body.expiresAt !== undefined) {
    return { kind: 'signed_in', accessToken: body.accessToken, expiresAt: body.expiresAt };
  }
  throw new Error('Unrecognised login response from the server');
}

/** Commits a successful sign in: the token lives in memory, nowhere else. */
function commit(result: LoginResult): LoginResult {
  if (result.kind === 'signed_in') {
    setAccessToken({ value: result.accessToken, expiresAt: result.expiresAt });
  }
  return result;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  if (isMockTransport()) {
    const { mockLogin } = await import('@/mocks/handlers');
    return commit(await mockLogin(email, password));
  }
  const body = await request<LoginWireResponse>('/auth/login', {
    method: 'POST',
    anonymous: true,
    body: { email, password },
  });
  return commit(toLoginResult(body));
}

export async function verifyTotp(mfaToken: string, code: string): Promise<LoginResult> {
  if (isMockTransport()) {
    const { mockVerifyTotp } = await import('@/mocks/handlers');
    return commit(await mockVerifyTotp(mfaToken, code));
  }
  const body = await request<LoginWireResponse>('/auth/mfa/totp', {
    method: 'POST',
    anonymous: true,
    body: { mfaToken, code },
  });
  return commit(toLoginResult(body));
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  if (isMockTransport()) {
    const { mockCurrentUser } = await import('@/mocks/handlers');
    return mockCurrentUser();
  }
  return request<CurrentUser>('/auth/me');
}

export async function signOut(): Promise<void> {
  try {
    if (isMockTransport()) {
      const { mockSignOut } = await import('@/mocks/handlers');
      await mockSignOut();
    } else {
      // The server revokes the user_session row and clears the refresh cookie. Even if
      // this call fails we still drop the in-memory token, because leaving a usable token
      // behind after the technician asked to sign out is the worse outcome.
      await request<void>('/auth/logout', { method: 'POST' });
    }
  } finally {
    clearAccessToken();
  }
}
